import _keyBy from 'lodash/keyBy';
import _mapValues from 'lodash/mapValues';
import _omitBy from 'lodash/omitBy';
import { groupBy, keyArrayBy } from 'src/utils/array';
import { getUuid } from 'src/utils/url';

import {
  API_RESOURCES,
  PAGINATION_IGNORE_DEFAULT_LIMIT,
  REFERENCE_TABLE_NAMES,
  WORK_INSTRUCTION_DOCUMENT_TYPES,
  WORK_INSTRUCTION_TYPES,
} from './constants';

/**
 * Returns checklists list for prints with extra prop `print` = Print_Uri
 *
 * @param api `ky` extended object, which is used to make api calls. See /utils/api.js for details.
 *             TODO: It maybe makes sense to import it directly from utils
 * @param prints Array List of Print objects (/print/ resource)
 */

export const getChecklistsForPrintsByPrintUri = async (api, prints) => {
  if (!prints.length) {
    return {};
  }

  const printUris = prints.map(print => print.uri);
  const workChecklistsFor = await api.getAllByFilter(
    printUris,
    `${API_RESOURCES.WORK_CHECKLIST_FOR_RESOURCE}/`,
    {},
    'related_uri'
  );

  const workChecklistsForResourcesByPrintUri = _keyBy(workChecklistsFor, 'related_uri');
  return _omitBy(
    _mapValues(
      workChecklistsForResourcesByPrintUri,
      resource =>
        resource.work_checklist_linkings.length &&
        resource.work_checklist_linkings[0].work_checklist
    ),
    item => !item
  );
};

/**
 * Adds document object as report (if instruction report is document-related report) and returns updated array.
 * Document response example (object, which is patched as "report" key) from /document/ service:
 *  {
 "content": null,
 "name": "document.png",
 "virus_scan": "unscanned",
 "related_table_name": "work_instruction_report",
 "related_uuid": "...",
 "upload_location": "https://...",
 "uri": "https://.../document/c7936f57-93b5-4563-8253-905d1e8c6381/"
 },
 *
 * @param workInstructionReports Array of work instruction reports to patch
 * @param documentByUUIDs Object of document records, uuid as key
 */
export const includeReportDocuments = (workInstructionReports, documentByUUIDs, reportTypes) => {
  if (!workInstructionReports.length) {
    return workInstructionReports;
  }

  return workInstructionReports.map(report => {
    const { report: value, work_instruction: workInstruction } = report;
    const reportType = reportTypes[workInstruction];
    const document = documentByUUIDs[value];

    if (document) {
      return {
        ...report,
        report: document,
      };
    }

    // Parse any report value that is valid JSON.
    // For now, this is for the range type.
    try {
      report.report = [WORK_INSTRUCTION_TYPES.TEXT, WORK_INSTRUCTION_TYPES.NUMBER].includes(
        reportType
      )
        ? value
        : JSON.parse(value);
    } catch (_) {
      // We can have invalid JSON for the `text` type, but in such cases, we must use it as it was provided.
    }

    return report;
  });
};

export const checkAllRequiredWorkInstructionsSubmitted = (
  requiredWorkInstructions,
  alreadySubmittedWorkInstructions
) => {
  // If there are no required work instructions, return true
  if (!requiredWorkInstructions.length) return true;
  // As we have the required work instructions, but no submitted work instructions, return false
  if (!alreadySubmittedWorkInstructions.length) return false;

  // Get the required UUIDs from the requiredWorkInstructions array
  const requiredUUIDs = requiredWorkInstructions
    .filter(instruction => instruction.required)
    .map(instruction => instruction.uuid);

  // Check if all required UUIDs are present in the alreadySubmittedWorkInstructions array
  return requiredUUIDs.every(uuid => alreadySubmittedWorkInstructions.includes(uuid));
};

export const getWorkInstructionReportsByPrintUri = async (api, prints, reportTypes) => {
  const printUris = prints.map(print => print['uri']);

  if (!printUris.length) {
    return {};
  }

  const workInstructionReports = await api.getAllByFilter(
    printUris,
    `${API_RESOURCES.WORK_INSTRUCTION_REPORT}/`,
    { searchParams: { 'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT } },
    'print'
  );

  const reportWithDocumentUuids = Object.values(workInstructionReports)
    .map(report => {
      const { work_instruction: instructionUuid, report: value } = report;
      const type = reportTypes[instructionUuid];

      if (WORK_INSTRUCTION_DOCUMENT_TYPES.includes(type) && value) {
        return instructionUuid;
      }

      return false;
    })
    .filter(Boolean);

  let documents = [];

  if (reportWithDocumentUuids.length > 0) {
    documents = await api.getAllByFilter(
      reportWithDocumentUuids,
      `${API_RESOURCES.DOCUMENT}/`,
      {
        searchParams: {
          'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
          'filter[related_table_name]': REFERENCE_TABLE_NAMES.WORK_INSTRUCTION_REPORT,
        },
      },
      'related_uuid'
    );
  }

  const reportsByPrintUri = groupBy(workInstructionReports, 'print');
  const documentContentsByUUID = keyArrayBy(documents, ({ uri }) => getUuid(uri));

  const workInstructionReportsByPrintUri = {};
  for (const print of prints) {
    const printReports = reportsByPrintUri[print.uri] || [];
    workInstructionReportsByPrintUri[print.uri] = includeReportDocuments(
      printReports,
      documentContentsByUUID,
      reportTypes
    );
  }
  return workInstructionReportsByPrintUri;
};
